import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { useNeedsAttentionForAdapter } from '@bingads-webui-campaign-react/attention-beacon';

export const ImageListPortalBeaconAdapter = (WrappedComponent) => {
  const HOC = ({
    containerState,
    isLogos,
    validationProps,
    attentionBeaconAdapterProps: attentionBeaconAdapterPropsFromProps,
    ...restProps
  }) => {
    const {
      imageAssetsLoadFailure,
      imageErrors,
      logoErrors,
      isDirty,
    } = containerState;

    const errorMessages = isLogos ? logoErrors : imageErrors;

    const needsAttentionLocal = useMemo(() => {
      const showValidationError = (isDirty || validationProps.alwaysShowError) && !_.isEmpty(errorMessages);
      return imageAssetsLoadFailure || showValidationError;
    }, [errorMessages, imageAssetsLoadFailure, isDirty, validationProps.alwaysShowError]);

    const { attentionBeaconAdapterProps } = useNeedsAttentionForAdapter({ needsAttentionLocal, attentionBeaconAdapterProps: attentionBeaconAdapterPropsFromProps });

    return (
      <WrappedComponent
        containerState={containerState}
        isLogos={isLogos}
        validationProps={validationProps}
        {...restProps}
        {...WrappedComponent.isAttentionBeaconAdapter ? { attentionBeaconAdapterProps } : {}}
      />
    );
  };

  HOC.propTypes = {
    isLogos: PropTypes.bool.isRequired,
    containerState: PropTypes.shape({
      imageAssetsLoadFailure: PropTypes.bool,
      imageErrors: PropTypes.arrayOf(PropTypes.string),
      logoErrors: PropTypes.arrayOf(PropTypes.string),
      isDirty: PropTypes.bool,
    }).isRequired,
    validationProps: PropTypes.shape({
      alwaysShowError: PropTypes.bool,
    }),
    attentionBeaconAdapterProps: PropTypes.shape({
      onAdapterNeedsAttentionChange: PropTypes.func.isRequired,
    }).isRequired,
  };

  HOC.defaultProps = {
    validationProps: {},
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `ValidationBeaconAdapter(${getDisplayName(WrappedComponent)})`;
  HOC.isAttentionBeaconAdapter = true;

  return HOC;
};
